import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { useEvents } from '../../hooks/useEvents';
import { safeFormat, isPastDate } from '../../utils/date';
import { getImageUrl } from '../../utils/helpers';

const EventsGallery: React.FC = () => {
    const { data: events, isLoading } = useEvents();

    if (isLoading) {
        return (
            <section id="acoes" className="py-24 bg-slate-50">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-[450px] animate-pulse">
                                <div className="h-48 bg-slate-200 rounded-t-2xl"></div>
                                <div className="p-6 space-y-4">
                                    <div className="h-4 w-1/3 bg-slate-100 rounded"></div>
                                    <div className="h-8 w-full bg-slate-200 rounded"></div>
                                    <div className="h-20 w-full bg-slate-100 rounded"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (!events || events.length === 0) {
        return (
            <section id="acoes" className="py-24 bg-slate-50">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-extrabold text-brand-darkGreen mb-4">Ações da Regional</h2>
                    <p className="text-slate-500 italic">Nenhum evento registrado no momento. Fique atento às novidades!</p>
                </div>
            </section>
        );
    }

    return (
        <section id="acoes" className="py-24 bg-slate-50">
            <div className="container mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <Reveal>
                        <span className="text-brand-green font-bold tracking-widest uppercase text-sm mb-4 block">
                            Agenda & Memória
                        </span>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-brand-darkGreen mb-6">
                            Ações da Regional
                        </h2>
                        <div className="text-slate-600">
                            <p>Confira os registros dos encontros realizados e fique por dentro das próximas agendas.</p>
                        </div>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                    {events.map((event, index) => {
                        const isPast = event.status === 'past' || isPastDate(event.date);
                        return (
                            <Reveal key={event.id} delay={index * 200}>
                                <Link 
                                    to={`/evento/${event.slug || event.id}`}
                                    className="group block bg-white rounded-2xl overflow-hidden shadow-lg card-premium border border-slate-100/50 flex flex-col h-full transition-all hover:shadow-xl hover:-translate-y-1"
                                >
                                    <div className="relative h-48 overflow-hidden img-shine">
                                        <img
                                            src={getImageUrl(event.image) || 'https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=2069&auto=format&fit=crop'}
                                            alt={event.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                                        <div className="absolute top-4 right-4">
                                            <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-md border shadow-lg ${isPast
                                                ? 'bg-slate-900/60 text-white border-white/20'
                                                : 'bg-brand-green/90 text-white border-brand-green/30'
                                                }`}>
                                                {isPast ? 'Realizado' : 'Próximo Evento'}
                                            </span>
                                        </div>
                                        
                                        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white text-[10px] font-bold uppercase tracking-widest">
                                            <span className="flex items-center gap-1">
                                                <i className="fas fa-calendar-alt text-brand-yellow"></i> {safeFormat(event.date)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <i className={`fas ${Number(event.is_online_only) === 1 ? 'fa-video' : 'fa-map-marker-alt'} text-brand-yellow`}></i> 
                                                {Number(event.is_online_only) === 1 ? 'Online' : event.location}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-6 flex flex-col flex-grow">
                                        <h3 className="text-xl font-bold text-brand-darkGreen mb-3 group-hover:text-brand-green transition-colors">
                                            {event.title}
                                        </h3>

                                        <p className="text-slate-600 text-sm mb-6 line-clamp-3 leading-relaxed">
                                            {event.description}
                                        </p>

                                        <div className="mt-auto">
                                            <div className={`inline-flex items-center gap-2 font-bold text-sm transition-all px-4 py-2 rounded-lg w-full justify-center ${
                                                isPast 
                                                ? 'text-slate-500 border border-slate-200 group-hover:bg-slate-50' 
                                                : 'bg-brand-green text-white shadow-md btn-shine group-hover:bg-brand-darkGreen'
                                            }`}>
                                                {isPast ? (
                                                    <><i className="fas fa-camera"></i> VER DETALHES</>
                                                ) : (
                                                    <><i className="fas fa-calendar-check"></i> PARTICIPAR AGORA</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default EventsGallery;